import React, {useState, useEffect} from "react";
import {AwarenessInterface} from "../interfaces";
import {useTranslation} from "react-i18next";
import {getLanguage} from "../../../lang/helpers";
import iconCheckCircle from "url:shared-assets/icons/success-500/check-circle.svg";
import iconCheck from "url:shared-assets/icons/checkmark.svg";
import iconRight from "url:shared-assets/icons/black/arrow-right.svg";
import iconLeft from "url:shared-assets/icons/white/arrow-left.svg";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../../components/Modal";
import CustomButton from "../../../components/CustomButton";
import Loading from "../../../components/Loading";
import AttentionBox from "../../../components/Boxes/AttentionBox";


interface Props {
  awareness?: AwarenessInterface;
  visionStatementActivated: string;
  save: Function;
  nextStep: Function;
  nextBarriers: boolean;
  readOnly: boolean;
}

const ClientAwareness = (props: Props)=> {
  const {t, i18n} = useTranslation(); // i18n translation hook
  const lang = getLanguage(i18n.language);
  const [showModal, setShowModal] = useState(false);
  const [important, setImportant] = useState(props.awareness ? props.awareness.important : 0);
  const [well, setWell] = useState(props.awareness ? props.awareness.well : 0);
  const [energy, setEnergy] = useState(props.awareness ? props.awareness.energy : 0);
  const [score, setScore] = useState(props.awareness ? props.awareness.score : 0);
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState(false);
  const [resultTxt, setResultTxt] = useState("");
  const [resultNext, setResultNext] = useState("");
  const [cta, setCta] = useState("");
  const [step, setStep] = useState(props.awareness ? 4 : 1);
  const [doneBefore, setDoneBefore] = useState(false);
  const visionStatementActivated = new Date(props.visionStatementActivated);
  const now = new Date();

  useEffect(() => {
    if (important > 0 && well > 0 && energy > 0) {
      setDoneBefore(true);
      calculateResult();
    }
  }, []);

  useEffect(() => {
    if (energy > 0 && changed) {
      const _score = calculateResult();
      const _awareness: AwarenessInterface = {
        important: important,
        well: well,
        energy: energy,
        score: _score,
        created: now.toISOString(),
      };
      props.save(_awareness);
    }
  }, [energy])

  const calculateResult = () => {
    let _score: number;
    let txt: string;
    let nxt: string;
    let _cta = "barriers";

    if (important === 4) {
      if (well === 4 && energy === 4) {
        _score = 10;
        txt = lang === "sv" ?
          "Du är redo! Det här målet känns perfekt. Det är viktigt för dig, det passar din situation och du har full energi att jobba med det. Kör!" :
          "You're all set! This goal feels perfect. It matters to you, it fits your situation and you have the energy to act. Let's go!"
        nxt = lang === "sv" ? "Utforska vad som kan stå i vägen för dig att nå målet" :
            "Explore what could get in your way."
      } else if (well >= 3) {
        if (energy >= 3) {
          _score = 9;
          txt = lang === "sv" ?
            "Riktigt bra! Det här målet betyder verkligen något för dig. Det passar din situation och du har energi att jobba med det." :
            "Strong position! This goal really matters to you. It fits your situation and you have energy to act."
          nxt = lang === "sv" ? "Utforska vad som kan stå i vägen för dig att nå målet" :
            "Explore what could get in your way."
        } else {
          _score = 7;
          txt = lang === "sv" ?
            "Stark grund! Det här målet betyder verkligen något och det passar din situation. Det som håller dig tillbaka just nu är energin." :
            "Good foundation! This goal really matters and it fits your situation. The main thing holding you back is energy."
          nxt = lang === "sv" ? "Ta reda på vad som tar din energi just nu." :
            "Find out what's costing you energy right now."
        }
      } else {
        if (energy >= 3) {
          _score = 7;
          txt = lang === "sv" ?
            "Stark grund! Det här målet är viktigt för dig och du har energin. Men det passar inte riktigt in i din situation just nu." :
            "Good foundation! This goal matters to you and you have the energy. But it doesn't quite fit your situation right now."
          nxt = lang === "sv" ? "Ta reda på vad det är som gör att målet inte riktigt passar." :
            "Find out what's making this hard to fit into your situation."
        } else {
          _score = 6;
          txt = lang === "sv" ?
            "Bra start! Det här målet är viktigt för dig. Men det passar inte riktigt in i din situation och din energi är låg just nu." :
            "Solid start! This goal matters to you. But it doesn't quite fit your situation and your energy is low right now."
          nxt = lang === "sv" ? "Ta reda på vad som står i vägen och vad som kostar dig energi" :
            "Find out what's making this hard to fit and what's draining your energy."
        }
      }
    } else if (important === 3) {
      if (well >= 3) {
        if (energy >= 3) {
          _score = 8;
          txt = lang === "sv" ?
            "Riktigt bra! Du känner engagemang, det här målet passar din situation och du har energin att gå framåt." :
            "Strong position! You feel engaged, this goal fits your situation and you have the energy to move forward."
          nxt = lang === "sv" ? "Utforska vad som kan stå i vägen för dig att nå målet" :
            "Explore what could get in your way."
        } else {
          _score = 7;
          txt = lang === "sv" ?
            "Stark grund! Du känner engagemang och det här målet passar din situation. Det som håller dig tillbaka just nu är energin." :
            "Good foundation! You feel engaged and this goal fits your situation. The main thing holding you back is energy."
          nxt = lang === "sv" ? "Ta reda på vad som tar din energi just nu." :
            "Find out what's costing you energy right now."
        }
      } else {
        if (energy >= 3) {
          _score = 7;
          txt = lang === "sv" ?
            "Stark grund! Det här målet är viktigt för dig och du har energin. Men det passar inte riktigt in i din situation just nu." :
            "Good foundation! You feel engaged and have the energy. But this goal doesn't quite fit your situation right now."
          nxt = lang === "sv" ? "Ta reda på vad det är som gör att målet inte riktigt passar." :
            "Find out what's making this hard to fit into your situation."
        } else {
          _score = 6;
          txt = lang === "sv" ?
            "Bra start! Det här målet är viktigt för dig. Men det passar inte riktigt in i din situation och din energi är låg just nu." :
            "Solid start! This goal matters to you. But it doesn't quite fit your situation and your energy is low right now."
          nxt = lang === "sv" ? "Ta reda på vad som står i vägen och vad som kostar dig energi" :
            "Find out what's making this hard to fit and what's draining your energy."
        }
      }
    } else {
      _cta = "editGoal";
      if (well >= 3) {
        if (energy >= 3) {
          _score = 5;
          txt = lang === "sv" ?
            "Det här målet passar din situation och du har energi. Men det känns inte tillräckligt meningsfullt än." :
            "This goal fits your situation and you have energy. But it doesn't feel meaningful enough yet."
          nxt = lang === "sv" ? "Överväg att skriva om ditt mål så att det känns mer meningsfullt." :
            "Consider rewriting your goal to make it feel more meaningful."
        } else {
          _score = 3;
          txt = lang === "sv" ?
            "Det här målet passar din situation. Men det känns inte meningsfullt och du har " +
            "inte så mycket energi att jobba med det just nu." :
            "This goal fits your situation. But it doesn't feel meaningful and you don't have the " +
            "energy to act on it right now."
          nxt = lang === "sv" ? "Tips. Skriv om ditt mål så att det känns mer personligt meningsfullt." :
            "Rewrite your goal so it feels more personally meaningful."
        }
      } else {
        if (energy >= 3) {
          _score = 3;
          txt = lang === "sv" ?
            "Du har energin, men det här målet känns varken meningsfullt eller relevant just nu." :
            "You have the energy, but this goal doesn't feel meaningful or relevant right now."
          nxt = lang === "sv" ? "Tips. Skriv om ditt mål så att det bättre speglar vad du faktiskt vill." :
            "Rewrite your goal so it better reflects what you actually want."
        } else {
          _score = 1;
          txt = lang === "sv" ?
            "Det här målet känns varken meningsfullt, relevant eller genomförbart just nu." :
            "This goal doesn't feel meaningful, relevant or workable right now."
          nxt = lang === "sv" ? "Tips är att du börjar om och skriver om målet från grunden." :
            "Start fresh. Rewrite your goal from the ground up."
        }
      }
    }

    setResultTxt(txt);
    setResultNext(nxt);
    setCta(_cta);
    setScore(_score);
    return _score;
  }

  const updateAnswer = (answer: number) => {
    if (step === 1) {
      setImportant(answer);
      setChanged(true);
    } else if (step === 2) {
      setWell(answer);
    } else if (step === 3) {
      setEnergy(answer);
      setSaving(true);
      setTimeout(() => {
        setSaving(false);
      }, 700);
    } else {
      return;
    }
    setTimeout(() => {
      setStep(step + 1);
    }, 500);
  }

  const renderAlternatives = () => {
    let options: Array<string> = [];
    let selectedAnswer = 0;
    if (step === 1) {
      options = lang === "sv" ? ["Mycket viktigt", "Ganska viktigt", "Inte så viktigt", "Inte alls viktigt"] :
        ["Very important", "Quite important", "Not that important", "Not important at all"];
      selectedAnswer = important;
    } else if (step === 2) {
      options = lang === "sv" ? ["Mycket bra", "Ganska bra", "Inte så bra", "Inte alls bra"] :
        ["Very well", "Quite well", "Not that well", "Not well at all"];
      selectedAnswer = well;
    } else if (step === 3) {
      options = lang === "sv" ? ["Mycket", "Tillräckligt för framsteg", "Inte så mycket", "Ingen alls"] :
        ["A lot", "Enough to make progress", "Only a little", "None at all"];
      selectedAnswer = energy;
    }

    return (
      <div className="mt-4 flex gap-4 flex-wrap">
        {options.map((option, index) => {
          const answer = 4 - index;
          let cls = "z-half py-8 flex items-center justify-center rounded-md z-title2 cursor-pointer ";
          if (answer === selectedAnswer) {
            cls += "bg-primary-500 text-indigo-900";
          } else {
            cls += "bg-indigo-800 hover:bg-indigo-700";
          }
          return (
            <div key={"answer_" + answer} onClick={() => updateAnswer(answer)}
                 className={cls}>
              {option}
            </div>
          )
        })
        }
      </div>
    )
  }

  const renderStep = () => {
    if (step === 1) {
      return (
        <>
          <div className="z-title1">
            {lang === "sv" ? "Hur viktigt är det för dig att nå den här målbilden?" :
              "How important is it for you to reach this goal?"}

          </div>
          {renderAlternatives()}
        </>

      )
    } else if (step === 2) {
      return (
        <>
          <div className="z-title1">
            {lang === "sv" ? "Hur väl passar den här målbilden din nuvarande situation?" :
              "How well does it connect to your current situation?"}
          </div>
          {renderAlternatives()}
        </>
      )
    } else if (step === 3) {
      return (
        <>
          <div className="z-title1">
            {lang === "sv" ? "Hur mycket energi har du att jobba med det här just nu?" :
              "How much energy do you have to work on this right now?"}
          </div>
          {renderAlternatives()}
        </>
      )
    } else if (step === 4) {
      if (saving) {
        return (
          <div style={{height: 264}}>
            <Loading txt={lang === "sv" ? "Laddar resultat" : "Loading result"}/>
          </div>

        )
      }
      return (
        <div className="bg-indigo-800 rounded-md p-4">
          {
            !doneBefore &&
              <div className="rounded-md border border-aquamarine-500 p-4 flex items-center gap-x-2">
                  <img src={iconCheckCircle} alt="check" className="h-6 w-6"/>
                  <div className="z-title3">{lang === "sv" ? "Analys klar" : "Analysis completed"}</div>
              </div>
          }

          <div className="flex mt-4 justify-between items-center">
            <div className="z-title1">{lang === "sv" ? "Din engagemangsnivå" : "Your engagement level"}</div>
            <div className="flex items-center">
              <div className="text-2xl font-medium">{score}</div>
              <div className="z-title2">/10</div>
            </div>
          </div>

          <div className="mt-2 z-body1">{resultTxt}</div>

          <div className="mt-4 z-title1">{lang === "sv" ? "Rekommenderat nästa steg" : "Recommended next step"}</div>

          <div className="mt-2 z-body1">{resultNext}</div>
        </div>
      )
    }
    return <>Missing step {step}</>
  }

  const closeModal = () => {
    setShowModal(false);
    if (!doneBefore && props.nextBarriers) {
      setTimeout(() => {
        props.nextStep(cta);
      }, 300);
    }
  }

  const renderFooter = () => {
    if (step > 3) {
      return (
        <div className="flex gap-x-2">

          <CustomButton disabled={props.readOnly} buttonType="outlineWhiteBase" size="sm"
                        onClick={() => {
                          setImportant(0);
                          setWell(0);
                          setEnergy(0);
                          setStep(1);
                          // setDoneBefore(false);
                        }}>
            {lang === "sv" ? "Börja om" : "Restart"}
          </CustomButton>

          {
            (props.nextBarriers && !doneBefore) ? (
              <CustomButton disabled={props.readOnly} buttonType="blueBase" size="sm"
                            rightIcon={<img src={iconRight} alt="right"/>}
                            onClick={() => closeModal()}>
                {
                  cta === "editGoal" ? lang === "sv" ? "Redigera målbilden" : "Review goal"
                    :   lang === "sv" ? "Utforska hinder" : "Explore barriers"
                }
              </CustomButton>
              ) : (
              <CustomButton buttonType="blueBase" size="sm"
                            onClick={() => closeModal()}>
                {t("done")}
              </CustomButton>
            )

          }

        </div>

      )

    } else if (step > 1) {
      return (
        <CustomButton buttonType="outlineWhiteBase" size="sm" leftIcon={<img src={iconLeft} alt="back"/>}
                      onClick={() => setStep(step - 1)}>
          {t("back")}
        </CustomButton>
      )
    }
  }

  return (
    <div className="bg-indigo-800 mt-4 p-4 rounded-md hover:bg-indigo-700 border-indigo-700 border-1">
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          {
            props.awareness ? (
              <div className="rounded-sm p-2 bg-success-500 w-11 text-center">
                <img src={iconCheck} alt="check" className="h-6 w-6"/>
              </div>
            ) : (
              <div className="rounded-sm p-2 bg-indigo-700 border border-indigo-600 w-11 z-title3 text-center">1</div>
            )
          }

          <div className="ml-3">
            <div className="z-title1">{t("quick-pulse")}</div>
            <div className="z-body1">
              {lang === "sv" ? "Hur viktigt är målet och finns energin?" : "Check your readiness and engagement level"}

            </div>
          </div>
        </div>
        <CustomButton disabled={(props.readOnly && !props.awareness)}
                      buttonType={props.awareness ? "transparentBase" : "blueBase"} size="sm"
                      rightIcon={props.awareness ? null : <img src={iconRight} alt="right"/>}
                      onClick={() => setShowModal(true)} style={{width: "unset"}}>
          {props.awareness ? lang === "sv" ? "Visa" : "View" : lang === "sv" ? "Starta" : "Start"}
        </CustomButton>
      </div>
      {
        (!props.awareness && (now.getTime() - visionStatementActivated.getTime() > 7 * 24 * 3600 * 1000)) &&
          <AttentionBox message={lang === "sv" ? "Gör klar din snabb puls" : "Complete your quick pulse"}/>
      }

      {
        showModal &&
          <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
              <ModalHeader onClose={() => setShowModal(false)}>{t("quick-pulse")}</ModalHeader>
              <ModalBody>
                <div style={{minHeight: 264}}>
                  {
                    step < 4 &&
                      <div className="h-2 mb-4 rounded-full bg-indigo-700" style={{width: "100%"}}>
                          <div className="h-2 rounded-full bg-primary-500"
                               style={{width: step * 25 + "%"}}></div>
                      </div>
                  }
                  {renderStep()}
                </div>
              </ModalBody>
              <ModalFooter>
                <div style={{minHeight: 38, width: "100%"}}>
                  {renderFooter()}
                </div>
              </ModalFooter>
          </Modal>
      }
    </div>
  )
}

export default ClientAwareness;